import React from "react";
import { Users, Download, Search } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/**
 * Pick customers and download one calibration-due PDF report PER customer.
 * - Defaults to every customer with equipment due in the current window.
 * - "Select all" (no explicit ids) → backend includes every due customer and
 *   returns a ZIP (one PDF per client). A single selected customer → one PDF.
 */
export function CalDueByCustomerModal({ filter, days, windowLabel, onClose }) {
  const [loading, setLoading] = React.useState(true);
  const [customers, setCustomers] = React.useState([]); // {customer_id, customer_name, count}
  const [selected, setSelected] = React.useState(new Set());
  const [q, setQ] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/calibration-due", { params: { filter, days, limit: 2000 } })
      .then((r) => {
        if (!alive) return;
        const items = r.data?.items || [];
        const m = new Map();
        items.forEach((it) => {
          const id = it.customer_id || "unknown";
          if (!m.has(id)) m.set(id, { customer_id: id, customer_name: it.customer_name || "Unknown customer", count: 0 });
          m.get(id).count += 1;
        });
        const list = [...m.values()].sort((a, b) => a.customer_name.localeCompare(b.customer_name));
        setCustomers(list);
        setSelected(new Set(list.map((c) => c.customer_id)));
      })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [filter, days]);

  const filtered = customers.filter((c) => c.customer_name.toLowerCase().includes(q.toLowerCase()));
  const allFilteredSel = filtered.length > 0 && filtered.every((c) => selected.has(c.customer_id));
  const totalEquip = customers.filter((c) => selected.has(c.customer_id)).reduce((s, c) => s + c.count, 0);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllFiltered = () => setSelected((s) => {
    const n = new Set(s);
    if (allFilteredSel) filtered.forEach((c) => n.delete(c.customer_id));
    else filtered.forEach((c) => n.add(c.customer_id));
    return n;
  });

  const download = async () => {
    if (!selected.size) { toast.error("Select at least one customer."); return; }
    setBusy(true);
    try {
      const allSelected = selected.size === customers.length;
      const params = { filter, days };
      if (!allSelected) params.customer_ids = [...selected].join(",");
      const res = await api.get("/calibration-due/export-by-customer", {
        params, responseType: "blob", timeout: 300000,
      });
      const ct = res.headers["content-type"] || "";
      const isZip = ct.includes("zip");
      // Try to honour the server filename
      let fname = `calibration-due-by-customer-${windowLabel}-${new Date().toISOString().slice(0, 10)}.${isZip ? "zip" : "pdf"}`;
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      if (m) fname = m[1];
      const blob = new Blob([res.data], { type: isZip ? "application/zip" : "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(isZip
        ? `Downloaded ${selected.size} customer reports (ZIP) — check your downloads`
        : "Report ready — check your downloads");
      onClose();
    } catch (e) {
      // blob error bodies need decoding
      if (e?.response?.data instanceof Blob) {
        try { const t = JSON.parse(await e.response.data.text()); toast.error(t.detail || "Export failed"); }
        catch { toast.error(apiErr(e)); }
      } else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Users} title="Export calibration-due — per customer"
      subtitle="One report per client, downloaded as PDF (or a ZIP for several)."
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="caldue-bycust-cancel">Cancel</button>
        <button className="btn gold" onClick={download} disabled={busy || !selected.size} data-testid="caldue-bycust-download">
          <Download size={14} /> {busy ? "Preparing…" : `Download (${selected.size} report${selected.size === 1 ? "" : "s"})`}
        </button>
      </>}>
      {loading ? (
        <p className="t-mut" style={{ padding: "10px 0" }}>Loading customers…</p>
      ) : customers.length === 0 ? (
        <p className="t-mut" style={{ padding: "10px 0" }}>No equipment due in this window.</p>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <Search size={14} style={{ position: "absolute", left: 9, top: 9, color: "var(--text-3)" }} />
              <input className="input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Filter customers…" style={{ width: "100%", paddingLeft: 28 }}
                data-testid="caldue-bycust-search" />
            </div>
            <button className="btn ghost sm" onClick={toggleAllFiltered} data-testid="caldue-bycust-selectall">
              {allFilteredSel ? "Clear" : "Select"} {q ? "shown" : "all"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 8 }} data-testid="caldue-bycust-summary">
            <b>{selected.size}</b> of {customers.length} customers selected · <b>{totalEquip}</b> equipment item(s)
          </div>
          <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8 }}>
            {filtered.map((c) => (
              <label key={c.customer_id} data-testid={`caldue-bycust-row-${c.customer_id}`}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                  borderBottom: "1px solid var(--line)", cursor: "pointer" }}>
                <input type="checkbox" checked={selected.has(c.customer_id)}
                  onChange={() => toggle(c.customer_id)} data-testid={`caldue-bycust-check-${c.customer_id}`} />
                <span className="t-strong" style={{ flex: 1 }}>{c.customer_name}</span>
                <span className="t-mut" style={{ fontSize: 12, minWidth: 54, textAlign: "right" }}>{c.count} item{c.count === 1 ? "" : "s"}</span>
              </label>
            ))}
            {!filtered.length && <div className="t-mut" style={{ padding: 14, textAlign: "center" }}>No customers match "{q}".</div>}
          </div>
        </>
      )}
    </Modal>
  );
}
